import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@apollo/client/react';
import { ANALYTICS_ENTITY_CATALOG, ANALYTICS_CATALOG } from '../../../graphql/analytics';
import { useJoinFields } from '../useJoinFields';
import { availableFields, graphEntities, graphJoins } from './flowFields';
import { addNode, emptyGraph, findNode, makeNode, removeNode, updateParams } from './graphModel';

const AUTOSAVE_DELAY = 600;

// Owns the graph being edited: local state, debounced autosave, node
// operations and the field/catalog context the inspector needs.
export function useWorkflowEditor({ workflow, metas, onPersist }) {
  const [graph, setGraph] = useState(() => (workflow && workflow.graph) || emptyGraph());
  const [selectedId, setSelectedId] = useState(null);
  const timer = useRef(null);

  useEffect(() => {
    clearTimeout(timer.current);
    setGraph((workflow && workflow.graph) || emptyGraph());
    setSelectedId(null);
  }, [workflow && workflow.id]);

  useEffect(() => () => clearTimeout(timer.current), []);

  const commit = useCallback(
    (next) => {
      setGraph(next);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => onPersist(next), AUTOSAVE_DELAY);
    },
    [onPersist]
  );

  const entity = graphEntities(graph)[0];
  const joins = graphJoins(graph);
  const { data: entityData } = useQuery(ANALYTICS_ENTITY_CATALOG, {
    variables: { entity },
    skip: !entity
  });
  const entityCatalog = entityData ? entityData.analyticsEntityCatalog : null;
  const joinFields = useJoinFields(entity, joins);
  const { data: catalogData } = useQuery(ANALYTICS_CATALOG);

  const context = useMemo(
    () => ({
      joins,
      entities: entityCatalog?.entities || catalogData?.analyticsCatalog?.entities || [],
      entityJoins: entityCatalog?.joins || [],
      chartTypes: catalogData?.analyticsCatalog?.chartTypes || []
    }),
    [joins, entityCatalog, catalogData]
  );

  const fieldsFor = useCallback(
    (id) =>
      availableFields({
        graph,
        result: workflow && workflow.result,
        nodeId: id,
        entityCatalog,
        joinFields
      }),
    [graph, workflow, entityCatalog, joinFields]
  );

  // Every graph change goes through commit, so adding or deleting a node is
  // autosaved exactly like a parameter edit.
  const addNodeType = useCallback(
    (type) => {
      const meta = metas[type];
      const node = makeNode(type, defaultsFor(meta, graph), nextPosition(graph, selectedId));
      commit(addNode(graph, node, attachFrom(graph, selectedId, metas)));
      setSelectedId(node.id);
    },
    [commit, graph, metas, selectedId]
  );

  const deleteNode = useCallback(
    (id) => {
      commit(removeNode(graph, id));
      if (id === selectedId) setSelectedId(null);
    },
    [commit, graph, selectedId]
  );

  return {
    graph,
    changeGraph: commit,
    selectedId,
    setSelectedId,
    selectedNode: findNode(graph, selectedId),
    selectedMeta: selectedNodeMeta(graph, selectedId, metas),
    addNodeType,
    deleteNode,
    changeParams: (id, params) => commit(updateParams(graph, id, params)),
    context,
    fieldsFor
  };
}

function selectedNodeMeta(graph, id, metas) {
  const node = findNode(graph, id);
  return node ? metas[node.type] : null;
}

// New nodes start from the defaults the server declares.
function defaultsFor(meta, graph) {
  const params = {};
  ((meta && meta.params) || []).forEach((param) => {
    if (param.default != null) params[param.key] = param.default;
    if (param.type === 'entity') params[param.key] = graphEntities(graph)[0] || 'grave';
    if (param.type === 'chartType') params[param.key] = 'bar';
  });
  return params;
}

// Drop new nodes to the right of the selection, or below the graph.
function nextPosition(graph, selectedId) {
  const selected = findNode(graph, selectedId);
  if (selected) return { x: selected.position.x + 260, y: selected.position.y };
  const bottom = (graph.nodes || []).reduce((max, node) => Math.max(max, node.position.y), 0);
  return { x: 60, y: (graph.nodes || []).length ? bottom + 130 : 60 };
}

// Connect a new node to the selected one so the palette builds a pipeline.
function attachFrom(graph, selectedId, metas) {
  const selected = findNode(graph, selectedId);
  if (!selected) return null;
  return metas[selected.type] && metas[selected.type].terminal ? null : selected.id;
}
